import { looksLikeProductScan, type ProductScanLike } from '@/lib/scanning/retailCode';

/**
 * The pure half of the standard Receive screen: what a scan means while the camera stays open,
 * how the checklist is ordered, and how the server's outcome is shown. Kept free of React so
 * receiveScan.test.ts can pin every rule a receiver depends on.
 *
 * A camera re-read of the same serial inside the window is ignored, so a label left in frame
 * does not raise "already checked". A different serial of a product that is already checked is
 * sent, and the server answers `alreadyConfirmed`.
 */

export const RECEIVE_CAMERA_REREAD_WINDOW_MS = 2500;
export const RECEIVE_HINT_INTERVAL_MS = 4000;
const RECENT_READS_MAX = 8;

export type ReceiveLine = {
  productId: string;
  product?: {
    title?: string | null;
    fdaProduct?: {
      elevenDigitNdc?: string | null;
      deaSchedule?: string | null;
    } | null;
    upcProduct?: { upc?: string | null } | null;
  } | null;
  productName: string;
  ndc?: string | null;
  deaSchedule?: string | null;
  orderedQuantity: number;
  shippedQuantity: number;
  /** EPCIS listed this many more units than ordered (0 / absent = none). */
  epcisOverageQuantity?: number | null;
  status: string;
  confirmedByName?: string | null;
};

export type ReceiveList = {
  purchaseOrderId: string;
  clientPurchaseOrderNumber?: string | null;
  vendorName?: string | null;
  lines: ReceiveLine[];
  confirmedCount: number;
  openCount: number;
  notInShipmentCount: number;
  canComplete: boolean;
  completeBlockedReason?: string | null;
};

export type ReceiveScanOutcome = 'confirmed' | 'alreadyConfirmed' | 'container' | 'notOnPo';

export type ReceiveScanResult = {
  outcome: string;
  productId?: string | null;
  productName?: string | null;
  ndc?: string | null;
};

/** Open products first, then ones missing from the shipment, then checked. Stable within a group. */
export const orderReceiveRows = <T extends { status: string }>(lines: readonly T[]): T[] => {
  const rank = (status: string) =>
    status === 'confirmed' ? 2 : status === 'notInShipment' ? 1 : 0;
  return lines
    .map((line, index) => ({ line, index }))
    .sort((a, b) => rank(a.line.status) - rank(b.line.status) || a.index - b.index)
    .map(({ line }) => line);
};

export const receiveLineStatusLabel = (line: { status: string }): string | null => {
  if (line.status === 'confirmed') return 'Received ✓';
  if (line.status === 'notInShipment') return 'Not in shipment';
  return null;
};

/** WarehouseProductDisplay's shape: the catalog product, or one built from the line when it is gone. */
export const receiveLineProduct = (line: ReceiveLine): ReceiveLine['product'] => {
  const product = line.product || null;
  if (product) return { ...product, title: product.title?.trim() || line.productName };
  return {
    title: line.productName,
    fdaProduct:
      line.ndc || line.deaSchedule
        ? { elevenDigitNdc: line.ndc ?? null, deaSchedule: line.deaSchedule ?? null }
        : null
  };
};

export type ReceiveGateState = {
  recentCameraReads: { key: string; at: number }[];
  lastHintAt: number | null;
};

export const initialReceiveGateState = (): ReceiveGateState => ({
  recentCameraReads: [],
  lastHintAt: null
});

export type ReceiveScanJob = {
  serialNumber: string;
  gtin: string | null;
  manualEntry: boolean;
  /** The scan that opened this screen. A mixed container is silent — it only identified the PO. */
  opening: boolean;
  key: string;
};

export type ReceiveScanLike = ProductScanLike & {
  source?: string | null;
  opening?: boolean;
};

export type ReceiveScanDecision =
  | {
      type: 'ignore';
      reason: 'empty' | 'paused' | 'manual-open' | 'camera-reread' | 'in-flight' | 'hint-rate-limited';
    }
  | { type: 'hint'; title: string; message: string }
  | { type: 'enqueue'; job: ReceiveScanJob };

const trimmed = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

export const receiveScanKey = (serialNumber: string, gtin: string | null): string =>
  `${gtin || ''}|${serialNumber}`;

const rememberCameraRead = (
  state: ReceiveGateState,
  key: string,
  now: number
): ReceiveGateState => ({
  ...state,
  recentCameraReads: [
    ...state.recentCameraReads.filter(
      (read) => read.key !== key && now - read.at <= RECEIVE_CAMERA_REREAD_WINDOW_MS
    ),
    { key, at: now }
  ].slice(-RECENT_READS_MAX)
});

const cameraStillSees = (state: ReceiveGateState, key: string, now: number) =>
  state.recentCameraReads.some(
    (read) => read.key === key && now - read.at <= RECEIVE_CAMERA_REREAD_WINDOW_MS
  );

/**
 * What a camera, handheld or typed scan means right now. `inFlight` is the set of keys already
 * queued, so a second read of one of them is dropped until the server has answered.
 */
export const classifyReceiveScan = (
  state: ReceiveGateState,
  scan: ReceiveScanLike,
  {
    manualOpen,
    paused,
    inFlight,
    now
  }: {
    manualOpen: boolean;
    paused: boolean;
    inFlight: ReadonlySet<string>;
    now: number;
  }
): { state: ReceiveGateState; decision: ReceiveScanDecision } => {
  if (!scan) return { state, decision: { type: 'ignore', reason: 'empty' } };
  if (paused) return { state, decision: { type: 'ignore', reason: 'paused' } };
  const manual = scan.isManualEntry === true;
  if (manualOpen && !manual) return { state, decision: { type: 'ignore', reason: 'manual-open' } };

  const serialNumber = trimmed(scan.serialNumber);
  const gtin = trimmed(scan.gtin) || null;
  const source = manual ? 'manual' : scan.source === 'hardware' ? 'hardware' : 'camera';

  if (!serialNumber) {
    if (!trimmed(scan.text) && !gtin && !scan.sscc) {
      return { state, decision: { type: 'ignore', reason: 'empty' } };
    }
    if (
      source === 'camera' &&
      state.lastHintAt != null &&
      now - state.lastHintAt < RECEIVE_HINT_INTERVAL_MS
    ) {
      return { state, decision: { type: 'ignore', reason: 'hint-rate-limited' } };
    }
    const productCode = looksLikeProductScan(scan);
    return {
      state: { ...state, lastHintAt: now },
      decision: {
        type: 'hint',
        title: productCode ? 'Scan the barcode on the case' : "That isn't a product barcode",
        message: productCode
          ? 'A product code alone does not identify the case. Scan the 2D barcode or the SSCC.'
          : 'Scan a case, inner pack, unit or SSCC from this shipment.'
      }
    };
  }

  const key = receiveScanKey(serialNumber, gtin);
  if (inFlight.has(key)) return { state, decision: { type: 'ignore', reason: 'in-flight' } };

  const opening = scan.opening === true;
  if (source === 'camera' && !opening && cameraStillSees(state, key, now)) {
    return {
      state: rememberCameraRead(state, key, now),
      decision: { type: 'ignore', reason: 'camera-reread' }
    };
  }

  return {
    state: source === 'camera' || opening ? rememberCameraRead(state, key, now) : state,
    decision: {
      type: 'enqueue',
      job: {
        serialNumber,
        gtin,
        manualEntry: manual,
        opening,
        key
      }
    }
  };
};

export type ReceiveFeedback =
  | { kind: 'success'; productId: string }
  | { kind: 'silent' }
  | { kind: 'alert'; title: string; text: string; tone: 'info' | 'fail' };

/** How the screen reacts once the server has named the outcome. */
export const receiveOutcomeFeedback = (
  result: ReceiveScanResult,
  { opening }: { opening: boolean }
): ReceiveFeedback => {
  if (result.outcome === 'confirmed' && result.productId) {
    return { kind: 'success', productId: result.productId };
  }
  if (result.outcome === 'container') {
    if (opening) return { kind: 'silent' };
    return {
      kind: 'alert',
      tone: 'fail',
      title: 'Several products',
      text: 'This pallet or case holds several products — scan one case, inner pack or unit of each.'
    };
  }
  if (result.outcome === 'alreadyConfirmed') {
    const name = result.productName?.trim() || 'That product';
    const ndc = result.ndc ? ` (NDC ${result.ndc})` : '';
    return {
      kind: 'alert',
      tone: 'info',
      title: 'Already checked',
      text: `${name}${ndc} is already checked — scan a different product.`
    };
  }
  if (result.outcome === 'notOnPo') {
    const name = result.productName?.trim();
    return {
      kind: 'alert',
      tone: 'fail',
      title: 'Not on this PO',
      text: name
        ? `${name} is not on this purchase order.`
        : 'That product is not on this purchase order.'
    };
  }
  return {
    kind: 'alert',
    tone: 'fail',
    title: 'Could not check that scan',
    text: 'Scan a case, inner pack or unit from this shipment.'
  };
};

export type ReceiveScanQueue = {
  enqueue: (job: ReceiveScanJob) => void;
};

/** One scan at a time, in the order they were read, so a fast trigger pull is never dropped. */
export const createReceiveScanQueue = (
  run: (job: ReceiveScanJob) => Promise<void>
): ReceiveScanQueue => {
  let chain = Promise.resolve();
  return {
    enqueue(job) {
      chain = chain.then(() => run(job)).catch(() => undefined);
    }
  };
};
